/**
 * Timeline utility functions — date/time helpers, member color, slot resolution
 */

import dayjs from 'dayjs'
import { Rotation } from '../../../../types/rotation'
import { OncallAssignmentResponse } from '../../../../types/response'
import { Override } from '../../../../services/overrides'
export { abbreviateName } from '@/utils/function'

// ============ TYPES ============

export interface TimeSlotInfo {
    time_from: string
    time_to: string
    index: number
}

export interface MemberSpan {
    memberName: string | null
    startIdx: number
    endIdx: number
    colSpan: number
}

export interface FinalSpan {
    memberName: string | null
    startIdx: number
    colSpan: number
    type: 'assignment' | 'no_assignment' | 'prediction' | 'override' | 'empty'
    timeFrom: string
    timeTo: string
}

// ============ CONSTANTS ============

export const GRID_COLS = '140px repeat(14, minmax(80px, 1fr))'
export const MIN_WIDTH = 'calc(140px + 14 * 80px)'
export const DAY_COUNT = 14

// ============ COLOR ============

export const getMemberColor = (name: string) => {
    let hash = 0
    for (let i = 0; i < name.length; i++) {
        hash = name.charCodeAt(i) + ((hash << 5) - hash)
    }
    const hue = ((hash % 360) + 360) % 360
    return {
        bg: `hsl(${hue}, 65%, 88%)`,
        text: `hsl(${hue}, 65%, 30%)`,
        timeTxt: `hsl(${hue}, 55%, 40%)`,
    }
}

// ============ TIME HELPERS ============

export const timeToMinutes = (time: string): number => {
    const [h, m] = time.split(':').map(Number)
    if (h === 24) return 24 * 60
    return h * 60 + (m || 0)
}

export const extractTime = (timestamp: string): string => {
    if (!timestamp) return '00:00'
    const timePart = timestamp.includes('T')
        ? timestamp.split('T')[1]
        : timestamp
    return timePart?.slice(0, 5) || '00:00'
}

export const isPastOrToday = (date: Date): boolean =>
    dayjs(date).isBefore(dayjs(), 'day') || dayjs(date).isSame(dayjs(), 'day')

export const getRotationDays = (frequency: string | undefined): number => {
    switch (frequency) {
        case 'weekly':
            return 7
        case 'bi_weekly':
            return 14
        case 'monthly':
            return 30
        default:
            return 1
    }
}

// ============ SLOT RESOLUTION ============

export const getSlotsForDay = (
    rotation: Rotation,
    dayDate: Date
): TimeSlotInfo[] => {
    if (!rotation.is_active || rotation.member_count === 0) return []
    const dayOfWeek = dayjs(dayDate).day()
    if (!rotation.timing || rotation.timing.timing_type === 'everyday') {
        return [{ time_from: '00:00', time_to: '24:00', index: 0 }]
    }
    if (rotation.timing.timing_type === 'custom_days') {
        return rotation.timing.time_slots
            .map((slot, index) => {
                const isInRange =
                    slot.day_from <= slot.day_to
                        ? dayOfWeek >= slot.day_from && dayOfWeek <= slot.day_to
                        : dayOfWeek >= slot.day_from || dayOfWeek <= slot.day_to
                return isInRange
                    ? {
                          time_from: slot.time_from?.slice(0, 5) || '00:00',
                          time_to: slot.time_to?.slice(0, 5) || '24:00',
                          index,
                      }
                    : null
            })
            .filter(Boolean) as TimeSlotInfo[]
    }
    return []
}

export const getRotationSlotTime = (rotation: Rotation) =>
    rotation.timing?.timing_type === 'everyday'
        ? { time_from: '00:00', time_to: '24:00' }
        : rotation.timing?.time_slots?.[0]
          ? {
                time_from:
                    rotation.timing.time_slots[0].time_from?.slice(0, 5) ||
                    '08:30',
                time_to:
                    rotation.timing.time_slots[0].time_to?.slice(0, 5) ||
                    '17:30',
            }
          : { time_from: '08:30', time_to: '17:30' }

// ============ MEMBER ASSIGNMENT ============

export const getMemberForDay = (
    rotation: Rotation,
    dayIndex: number,
    days: Date[]
): string | null => {
    if (!rotation.members || rotation.members.length === 0) return null
    const rotationDays = getRotationDays(rotation.shift_frequency)
    const refDate = rotation.rotation_start_date || rotation.created_at
    const actualDate = days[dayIndex]

    if (rotationDays === 7) {
        const dow = dayjs(actualDate).day()
        const weekStart = dayjs(actualDate).startOf('week')
        let activeDayInWeek = 0
        for (let d = 0; d < 7; d++) {
            const checkDate = weekStart.add(d, 'day')
            const slots = getSlotsForDay(rotation, checkDate.toDate())
            if (slots.length > 0) {
                if (checkDate.day() === dow) break
                activeDayInWeek++
            }
        }
        return (
            rotation.members[activeDayInWeek % rotation.members.length]
                ?.employee_name || null
        )
    }

    if (!refDate || !actualDate) {
        return (
            rotation.members[dayIndex % rotation.members.length]
                ?.employee_name || null
        )
    }

    const startDay = dayjs(refDate).startOf('day')
    const totalDays = dayjs(actualDate).startOf('day').diff(startDay, 'day')
    let activeCount = 0
    for (let i = 0; i < totalDays; i++) {
        const slots = getSlotsForDay(rotation, startDay.add(i, 'day').toDate())
        if (slots.length > 0) activeCount++
    }

    return (
        rotation.members[activeCount % rotation.members.length]
            ?.employee_name || null
    )
}

// ============ SPAN BUILDERS ============

export const getMemberSpans = (
    rotation: Rotation,
    days: Date[]
): MemberSpan[] => {
    if (!rotation.members || rotation.members.length === 0) return []

    const spans: MemberSpan[] = []
    let currentMember: string | null = null
    let startIdx = 0

    for (let i = 0; i < days.length; i++) {
        const member = getMemberForDay(rotation, i, days)
        const slots = getSlotsForDay(rotation, days[i])

        if (slots.length === 0) {
            if (currentMember !== null) {
                spans.push({
                    memberName: currentMember,
                    startIdx,
                    endIdx: i - 1,
                    colSpan: i - startIdx,
                })
                currentMember = null
            }
            spans.push({
                memberName: null,
                startIdx: i,
                endIdx: i,
                colSpan: 1,
            })
            continue
        }

        if (member !== currentMember) {
            if (currentMember !== null) {
                spans.push({
                    memberName: currentMember,
                    startIdx,
                    endIdx: i - 1,
                    colSpan: i - startIdx,
                })
            }
            currentMember = member
            startIdx = i
        }
    }

    if (currentMember !== null) {
        spans.push({
            memberName: currentMember,
            startIdx,
            endIdx: days.length - 1,
            colSpan: days.length - startIdx,
        })
    }

    return spans
}

export const getFinalSpans = (
    rotation: Rotation,
    days: Date[],
    assignments: OncallAssignmentResponse[],
    overrides: Override[]
): FinalSpan[] => {
    const spans: FinalSpan[] = []
    let i = 0

    const getAssignment = (date: Date) => {
        const dateStr = dayjs(date).format('YYYY-MM-DD')
        return (
            assignments.find(
                (a) =>
                    a.oncall_date === dateStr &&
                    (a.oncall_rotation_id === rotation.oncall_rotation_id ||
                        (!a.oncall_rotation_id &&
                            rotation.oncall_rotation_name === 'Default'))
            ) || null
        )
    }

    const getOverrideForSlot = (
        date: Date,
        slotFrom: string,
        slotTo: string
    ) => {
        const dateStr = dayjs(date).format('YYYY-MM-DD')
        const slotStart = timeToMinutes(slotFrom)
        const slotEnd = timeToMinutes(slotTo)
        return (
            overrides.find((o) => {
                if (o.override_date !== dateStr) return false
                if (
                    o.oncall_rotation_id &&
                    o.oncall_rotation_id !== rotation.oncall_rotation_id
                )
                    return false
                const oStart = timeToMinutes(
                    o.time_from?.slice(0, 5) || '00:00'
                )
                const oEnd = timeToMinutes(o.time_to?.slice(0, 5) || '24:00')
                return oStart <= slotStart && oEnd >= slotEnd
            }) || null
        )
    }

    while (i < days.length) {
        const day = days[i]
        const slots = getSlotsForDay(rotation, day)

        if (slots.length === 0) {
            spans.push({
                memberName: null,
                startIdx: i,
                colSpan: 1,
                type: 'empty',
                timeFrom: '',
                timeTo: '',
            })
            i++
            continue
        }

        if (isPastOrToday(day)) {
            const assignment = getAssignment(day)
            const memberName = assignment?.employee_name || null
            const type = assignment ? 'assignment' : 'no_assignment'
            const timeFrom = assignment
                ? extractTime(assignment.oncall_start_time)
                : ''
            const timeTo = assignment
                ? extractTime(assignment.oncall_end_time)
                : ''

            let colSpan = 1
            while (i + colSpan < days.length) {
                const nextDay = days[i + colSpan]
                if (!isPastOrToday(nextDay)) break
                if (getSlotsForDay(rotation, nextDay).length === 0) break
                const nextAssignment = getAssignment(nextDay)
                if ((nextAssignment?.employee_name || null) !== memberName)
                    break
                colSpan++
            }

            spans.push({
                memberName,
                startIdx: i,
                colSpan,
                type,
                timeFrom,
                timeTo,
            })
            i += colSpan
        } else {
            const slot = slots[0]
            const override = getOverrideForSlot(
                day,
                slot.time_from,
                slot.time_to
            )

            if (override) {
                spans.push({
                    memberName: override.employee_name,
                    startIdx: i,
                    colSpan: 1,
                    type: 'override',
                    timeFrom: slot.time_from,
                    timeTo: slot.time_to,
                })
                i++
            } else {
                const memberName = getMemberForDay(rotation, i, days)
                let colSpan = 1
                while (i + colSpan < days.length) {
                    const nextDay = days[i + colSpan]
                    if (isPastOrToday(nextDay)) break
                    const nextSlots = getSlotsForDay(rotation, nextDay)
                    if (nextSlots.length === 0) break
                    if (
                        getOverrideForSlot(
                            nextDay,
                            nextSlots[0].time_from,
                            nextSlots[0].time_to
                        )
                    )
                        break
                    if (
                        getMemberForDay(rotation, i + colSpan, days) !==
                        memberName
                    )
                        break
                    colSpan++
                }
                spans.push({
                    memberName,
                    startIdx: i,
                    colSpan,
                    type: 'prediction',
                    timeFrom: slot.time_from,
                    timeTo: slot.time_to,
                })
                i += colSpan
            }
        }
    }

    return spans
}
